import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type ReactNode, type RefObject } from "react";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import { mergeAttributes, Node } from "@tiptap/core";
import type { EditorView } from "@tiptap/pm/view";
import StarterKit from "@tiptap/starter-kit";
import { TableKit } from "@tiptap/extension-table";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import { Color, FontSize, TextStyle } from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Link as LinkIcon,
  Undo2,
  Redo2,
  Highlighter,
  Baseline,
  SquareCode,
  Eye,
  Table,
  Quote,
  Trash2,
} from "lucide-react";
import { FILL_BLANK_SPAN, FILL_BLANK_TOKEN, decorateFillBlanks, undecorateFillBlanks } from "@/lib/questionScoring";
import {
  isMarkdownQuote,
  isMarkdownTable,
  looksLikeEscapedHtml,
  markdownQuoteToHtml,
  markdownTableToHtml,
  upgradeAuthorHtml,
} from "@/lib/markdownTables";
import StudentPreviewDialog from "@/components/courses/StudentPreviewDialog";
import "./RichTextEditor.css";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    preBlock: {
      togglePreBlock: () => ReturnType;
    };
  }
}

/**
 * LearnPath TipTap editor. Images and video stay typed body parts — paste of
 * those is stripped to plain text.
 */

const MEDIA_TAGS = /<(img|video|iframe|picture|source|audio|embed|object)\b/i;

const PreBlock = Node.create({
  name: "preBlock",
  group: "block",
  content: "inline*",
  defining: true,
  code: true,
  parseHTML() {
    return [{ tag: "pre", preserveWhitespace: "full" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["pre", mergeAttributes({ class: "al-cb-rte-pre" }, HTMLAttributes), 0];
  },
  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: (el) => el.getAttribute("style") || null,
        renderHTML: (attrs) => (attrs.style ? { style: attrs.style } : {}),
      },
    };
  },
  addCommands() {
    return {
      togglePreBlock:
        () =>
        ({ commands }) =>
          commands.toggleNode(this.name, "paragraph"),
    };
  },
});

const FillBlank = Node.create({
  name: "fillBlank",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  parseHTML() {
    return [{ tag: "span.al-cb-blank" }];
  },
  renderHTML() {
    return ["span", { class: "al-cb-blank", contenteditable: "false" }, FILL_BLANK_TOKEN];
  },
});

const HEADING_OPTIONS = [
  { id: "p", label: "Body" },
  { id: "1", label: "Title" },
  { id: "2", label: "Heading" },
  { id: "3", label: "Subhead" },
  { id: "4", label: "Caption" },
];

const FONT_SIZES = [
  { id: "", label: "Default" },
  { id: "0.85rem", label: "S" },
  { id: "1.05rem", label: "M" },
  { id: "1.35rem", label: "L" },
  { id: "1.75rem", label: "XL" },
];

const TEXT_COLORS = [
  { id: "", label: "Default", value: "" },
  { id: "#1c1917", label: "Ink", value: "#1c1917" },
  { id: "#57534e", label: "Gray", value: "#57534e" },
  { id: "#b91c1c", label: "Red", value: "#b91c1c" },
  { id: "#c2410c", label: "Orange", value: "#c2410c" },
  { id: "#047857", label: "Green", value: "#047857" },
  { id: "#1d4ed8", label: "Blue", value: "#1d4ed8" },
  { id: "#6d28d9", label: "Purple", value: "#6d28d9" },
  { id: "#be185d", label: "Pink", value: "#be185d" },
];

const HIGHLIGHT_COLORS = [
  { id: "", label: "None", value: "" },
  { id: "#fef08a", label: "Yellow", value: "#fef08a" },
  { id: "#bbf7d0", label: "Green", value: "#bbf7d0" },
  { id: "#bfdbfe", label: "Blue", value: "#bfdbfe" },
  { id: "#fbcfe8", label: "Pink", value: "#fbcfe8" },
  { id: "#fed7aa", label: "Orange", value: "#fed7aa" },
  { id: "#e7e5e4", label: "Gray", value: "#e7e5e4" },
];

function looksLikeHtml(value: string) {
  return /<\/?[a-z][\s\S]*>/i.test(String(value || ""));
}

export function toEditorHtml(value: string) {
  const raw = String(value || "");
  if (!raw.trim()) return "";
  if (looksLikeHtml(raw) || looksLikeEscapedHtml(raw)) return upgradeAuthorHtml(raw);
  if (isMarkdownTable(raw)) return markdownTableToHtml(raw);
  if (isMarkdownQuote(raw)) return markdownQuoteToHtml(raw);
  return `<p>${raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>")}</p>`;
}

function pastePlainText(view: EditorView, text: string) {
  if (!text) return;
  view.dispatch(view.state.tr.insertText(text));
}

function incomingHtml(value: string, allowBlanks: boolean) {
  const html = toEditorHtml(value);
  return allowBlanks ? decorateFillBlanks(html) : html;
}

function htmlHasText(value: string) {
  return Boolean(
    String(value || "")
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim(),
  );
}

function tableFromSelection(editor: Editor) {
  const { view, state } = editor;
  const pos = state.selection.from;
  const node = view.nodeDOM(pos);
  const start = node instanceof Element ? node : view.domAtPos(pos).node;
  const el = start instanceof Element ? start : start.parentElement;
  return el?.closest(".tableWrapper, table") ?? null;
}

function TableFloat({
  editor,
  hostRef,
  disabled,
}: {
  editor: Editor;
  hostRef: RefObject<HTMLDivElement | null>;
  disabled?: boolean;
}) {
  const floatRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    const place = () => {
      const host = hostRef.current;
      if (disabled || !host || !editor.isActive("table")) {
        setPos(null);
        return;
      }
      const table = tableFromSelection(editor);
      if (!table) {
        setPos(null);
        return;
      }
      const hostBox = host.getBoundingClientRect();
      const tableBox = table.getBoundingClientRect();
      if (tableBox.bottom < hostBox.top + 8 || tableBox.top > hostBox.bottom - 8) {
        setPos(null);
        return;
      }
      const barH = floatRef.current?.offsetHeight || 34;
      const gap = 6;
      const above = tableBox.top - hostBox.top - barH - gap;
      const below = tableBox.bottom - hostBox.top + gap;
      const fitsAbove = above >= 2;
      const fitsBelow = tableBox.bottom + barH + gap <= hostBox.bottom;
      // Prefer a gap above the table so the bar does not cover the header or cells.
      const top = fitsAbove ? above : fitsBelow ? below : Math.max(2, above);
      const right = Math.max(6, hostBox.right - Math.min(tableBox.right, hostBox.right));
      setPos({ top, right });
    };

    place();
    editor.on("selectionUpdate", place);
    editor.on("transaction", place);
    const host = hostRef.current;
    host?.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      editor.off("selectionUpdate", place);
      editor.off("transaction", place);
      host?.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [editor, hostRef, disabled]);

  if (!pos) return null;

  return (
    <div
      ref={floatRef}
      className="al-cb-rte-table-float"
      style={{ top: pos.top, right: pos.right }}
      role="toolbar"
      aria-label="Table"
    >
      <ToolbarButton
        label="Insert row"
        wide
        disabled={!editor.can().addRowAfter()}
        onClick={() => editor.chain().focus().addRowAfter().run()}
      >
        <span className="al-cb-rte-blank-label">+ Row</span>
      </ToolbarButton>
      <ToolbarButton
        label="Remove row"
        wide
        disabled={!editor.can().deleteRow()}
        onClick={() => editor.chain().focus().deleteRow().run()}
      >
        <span className="al-cb-rte-blank-label">− Row</span>
      </ToolbarButton>
      <ToolbarButton
        label="Insert column"
        wide
        disabled={!editor.can().addColumnAfter()}
        onClick={() => editor.chain().focus().addColumnAfter().run()}
      >
        <span className="al-cb-rte-blank-label">+ Col</span>
      </ToolbarButton>
      <ToolbarButton
        label="Remove column"
        wide
        disabled={!editor.can().deleteColumn()}
        onClick={() => editor.chain().focus().deleteColumn().run()}
      >
        <span className="al-cb-rte-blank-label">− Col</span>
      </ToolbarButton>
      <ToolbarButton
        label="Remove table"
        disabled={!editor.can().deleteTable()}
        onClick={() => editor.chain().focus().deleteTable().run()}
      >
        <Trash2 strokeWidth={2} size={15} />
      </ToolbarButton>
    </div>
  );
}

function ToolbarButton({
  active,
  disabled,
  onClick,
  label,
  wide,
  children,
}: {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  label: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`al-cb-rte-btn${wide ? " al-cb-rte-btn--wide" : ""}${active ? " al-cb-rte-btn--active" : ""}`}
      aria-label={label}
      title={label}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function ToolbarMenu({
  label,
  value,
  options,
  disabled,
  open,
  onToggle,
  onPick,
}: {
  label: string;
  value: string;
  options: { id: string; label: string }[];
  disabled?: boolean;
  open: boolean;
  onToggle: () => void;
  onPick: (id: string) => void;
}) {
  const current = options.find((opt) => opt.id === value);
  return (
    <div className="al-cb-rte-palette-wrap">
      <button
        type="button"
        className={`al-cb-rte-select-btn${open ? " al-cb-rte-btn--active" : ""}`}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={label}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onToggle}
      >
        <span>{current?.label || label}</span>
      </button>
      {open ? (
        <div className="al-cb-rte-menu" role="listbox" aria-label={label}>
          {options.map((opt) => (
            <button
              key={opt.id || "default"}
              type="button"
              role="option"
              aria-selected={opt.id === value}
              className={`al-cb-rte-menu-item${opt.id === value ? " al-cb-rte-menu-item--active" : ""}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onPick(opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function chainForMarks(editor: Editor) {
  const { empty, $from } = editor.state.selection;
  if (empty && $from.parent.type.name !== "preBlock") {
    const from = $from.start();
    const to = $from.end();
    if (to > from) return editor.chain().focus().setTextSelection({ from, to });
  }
  return editor.chain().focus();
}

function ColorPalette({
  colors,
  current,
  noneLabel,
  onPick,
  onCustom,
}: {
  colors: { id: string; label: string; value: string }[];
  current: string;
  noneLabel: string;
  onPick: (value: string) => void;
  onCustom?: (value: string) => void;
}) {
  return (
    <div className="al-cb-rte-palette" role="listbox">
      {colors.map((c) => (
        <button
          key={c.id || "none"}
          type="button"
          role="option"
          aria-selected={current === c.value}
          className={`al-cb-rte-swatch${current === c.value ? " al-cb-rte-swatch--active" : ""}${
            !c.value ? " al-cb-rte-swatch--none" : ""
          }`}
          title={c.label}
          aria-label={c.label}
          style={c.value ? { background: c.value } : undefined}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onPick(c.value)}
        >
          {!c.value ? noneLabel : null}
        </button>
      ))}
      {onCustom ? (
        <label className="al-cb-rte-swatch al-cb-rte-swatch--custom" title="Custom color">
          <input
            type="color"
            value={current && /^#/.test(current) ? current : "#1c1917"}
            aria-label="Custom color"
            onMouseDown={(e) => e.preventDefault()}
            onChange={(e) => onCustom(e.target.value)}
          />
        </label>
      ) : null}
    </div>
  );
}

export type RichTextEditorHandle = {
  insertHtml: (html: string) => void;
};

const RichTextEditor = forwardRef<
  RichTextEditorHandle,
  {
    value?: string;
    onChange?: (html: string) => void;
    disabled?: boolean;
    placeholder?: string;
    minHeight?: string;
    allowBlanks?: boolean;
    extraTools?: (editor: Editor) => ReactNode;
    /** Eye button. Off when a parent already previews the whole activity. */
    preview?: boolean;
  }
>(function RichTextEditor(
  {
    value = "",
    onChange,
    disabled = false,
    placeholder = "Write content…",
    minHeight = "8rem",
    allowBlanks = false,
    extraTools,
    preview = true,
  },
  ref,
) {
  const lastEmitted = useRef(incomingHtml(value, allowBlanks));
  const editorRef = useRef<Editor | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [openPalette, setOpenPalette] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const editor = useEditor({
    immediatelyRender: false,
    shouldRerenderOnTransaction: true,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
        codeBlock: false,
      }),
      TableKit.configure({
        table: { resizable: false, renderWrapper: true },
      }),
      PreBlock,
      FillBlank,
      Placeholder.configure({ placeholder }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      TextStyle,
      Color,
      FontSize,
      Highlight.configure({ multicolor: true }),
    ],
    content: incomingHtml(value, allowBlanks),
    editable: !disabled,
    onCreate: ({ editor: ed }) => {
      editorRef.current = ed;
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.isEmpty ? "" : ed.getHTML();
      lastEmitted.current = html;
      onChange?.(html);
    },
    editorProps: {
      attributes: {
        class: "al-cb-rte-prose",
        style: `min-height: ${minHeight}`,
      },
      handlePaste(_view, event) {
        const data = event.clipboardData;
        if (!data) return false;
        const hasMediaFile = [...data.items].some(
          (item) => item.type.startsWith("image/") || item.type.startsWith("video/"),
        );
        const html = data.getData("text/html");
        const plain = data.getData("text/plain");
        if (hasMediaFile || (html && MEDIA_TAGS.test(html))) {
          event.preventDefault();
          pastePlainText(_view, plain);
          return true;
        }
        if (isMarkdownTable(plain) && !/<table\b/i.test(html)) {
          event.preventDefault();
          editorRef.current?.chain().focus().insertContent(markdownTableToHtml(plain)).run();
          return true;
        }
        if (isMarkdownQuote(plain) && !/<blockquote\b/i.test(html)) {
          event.preventDefault();
          editorRef.current?.chain().focus().insertContent(markdownQuoteToHtml(plain)).run();
          return true;
        }
        return false;
      },
      handleDrop(_view, event) {
        const files = event.dataTransfer?.files;
        if (!files?.length) return false;
        if ([...files].some((file) => file.type.startsWith("image/") || file.type.startsWith("video/"))) {
          event.preventDefault();
          return true;
        }
        return false;
      },
    },
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editorRef.current = editor;
    editor.setEditable(!disabled, false);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const next = incomingHtml(value, allowBlanks);
    if (next === lastEmitted.current) return;
    if (allowBlanks && undecorateFillBlanks(next) === undecorateFillBlanks(lastEmitted.current)) {
      return;
    }
    lastEmitted.current = next;
    editor.commands.setContent(next || "", { emitUpdate: false });
  }, [editor, value, allowBlanks]);

  useImperativeHandle(
    ref,
    () => ({
      insertHtml: (html: string) => {
        if (!editor || editor.isDestroyed) return;
        editor.chain().focus().insertContent(html).run();
      },
    }),
    [editor],
  );

  useEffect(() => {
    if (!openPalette) return undefined;
    const close = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest?.(".al-cb-rte-palette-wrap")) return;
      setOpenPalette(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [openPalette]);

  if (!editor) return null;

  const headingValue = editor.isActive("heading", { level: 1 })
    ? "1"
    : editor.isActive("heading", { level: 2 })
      ? "2"
      : editor.isActive("heading", { level: 3 })
        ? "3"
        : editor.isActive("heading", { level: 4 })
          ? "4"
          : "p";
  const fontSize = String(editor.getAttributes("textStyle").fontSize || "");
  const textColor = String(editor.getAttributes("textStyle").color || "");
  const highlightColor = String(editor.getAttributes("highlight").color || "");
  const sizeOptions =
    fontSize && !FONT_SIZES.some((s) => s.id === fontSize)
      ? [...FONT_SIZES, { id: fontSize, label: fontSize }]
      : FONT_SIZES;

  const setLink = () => {
    const prev = String(editor.getAttributes("link").href || "");
    const url = window.prompt("Link URL", prev);
    if (url === null) return;
    if (!url.trim()) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };

  const previewHtml = incomingHtml(value, allowBlanks);
  const previewEmpty = !htmlHasText(value);

  return (
    <div className={`al-cb-rte${disabled ? " al-cb-rte--disabled" : ""}`}>
      <div className="al-cb-rte-toolbar" role="toolbar" aria-label="Formatting">
        <ToolbarButton
          label="Bold"
          active={editor.isActive("bold")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold strokeWidth={2} size={15} />
        </ToolbarButton>
        <ToolbarButton
          label="Italic"
          active={editor.isActive("italic")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic strokeWidth={2} size={15} />
        </ToolbarButton>
        <ToolbarMenu
          label="Text style"
          value={headingValue}
          options={HEADING_OPTIONS}
          disabled={disabled}
          open={openPalette === "heading"}
          onToggle={() => setOpenPalette((v) => (v === "heading" ? null : "heading"))}
          onPick={(next) => {
            const chain = editor.chain().focus();
            if (next === "p") chain.setParagraph().run();
            else chain.setHeading({ level: Number(next) as 1 | 2 | 3 | 4 }).run();
            setOpenPalette(null);
          }}
        />
        <ToolbarMenu
          label="Font size"
          value={fontSize}
          options={sizeOptions}
          disabled={disabled}
          open={openPalette === "size"}
          onToggle={() => setOpenPalette((v) => (v === "size" ? null : "size"))}
          onPick={(next) => {
            const chain = chainForMarks(editor);
            if (!next) chain.unsetFontSize().run();
            else chain.setFontSize(next).run();
            setOpenPalette(null);
          }}
        />
        <span className="al-cb-rte-sep" aria-hidden="true" />
        <ToolbarButton
          label="Bullet list"
          active={editor.isActive("bulletList")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List strokeWidth={2} size={15} />
        </ToolbarButton>
        <ToolbarButton
          label="Numbered list"
          active={editor.isActive("orderedList")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered strokeWidth={2} size={15} />
        </ToolbarButton>
        <ToolbarButton
          label="Quote"
          active={editor.isActive("blockquote")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <Quote strokeWidth={2} size={15} />
        </ToolbarButton>
        <ToolbarButton label="Link" active={editor.isActive("link")} disabled={disabled} onClick={setLink}>
          <LinkIcon strokeWidth={2} size={15} />
        </ToolbarButton>
        <ToolbarButton
          label="Insert table"
          active={editor.isActive("table")}
          disabled={disabled}
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
        >
          <Table strokeWidth={2} size={15} />
        </ToolbarButton>
        <ToolbarButton
          label="Code block"
          active={editor.isActive("preBlock")}
          disabled={disabled}
          onClick={() => editor.chain().focus().togglePreBlock().run()}
        >
          <SquareCode strokeWidth={2} size={15} />
        </ToolbarButton>
        {allowBlanks ? (
          <button
            type="button"
            className="al-cb-rte-btn al-cb-rte-btn--wide"
            aria-label="Insert blank"
            title="Insert blank"
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().insertContent(`${FILL_BLANK_SPAN}\u00a0`).run()}
          >
            <span className="al-cb-rte-blank-label">Blank</span>
          </button>
        ) : null}
        {extraTools ? extraTools(editor) : null}
        <span className="al-cb-rte-sep" aria-hidden="true" />
        <div className="al-cb-rte-palette-wrap">
          <ToolbarButton
            label="Text color"
            active={Boolean(textColor) || openPalette === "color"}
            disabled={disabled}
            onClick={() => setOpenPalette((v) => (v === "color" ? null : "color"))}
          >
            <span className="al-cb-rte-color-btn">
              <Baseline strokeWidth={2} size={15} />
              <span
                className="al-cb-rte-color-bar"
                style={{ background: textColor || "currentColor" }}
                aria-hidden="true"
              />
            </span>
          </ToolbarButton>
          {openPalette === "color" ? (
            <ColorPalette
              colors={TEXT_COLORS}
              current={textColor}
              noneLabel="×"
              onPick={(next) => {
                const chain = chainForMarks(editor);
                if (!next) chain.unsetColor().run();
                else chain.setColor(next).run();
                setOpenPalette(null);
              }}
              onCustom={(next) => {
                chainForMarks(editor).setColor(next).run();
                setOpenPalette(null);
              }}
            />
          ) : null}
        </div>
        <div className="al-cb-rte-palette-wrap">
          <ToolbarButton
            label="Highlight"
            active={editor.isActive("highlight") || openPalette === "highlight"}
            disabled={disabled}
            onClick={() => setOpenPalette((v) => (v === "highlight" ? null : "highlight"))}
          >
            <span className="al-cb-rte-color-btn">
              <Highlighter strokeWidth={2} size={15} />
              <span
                className="al-cb-rte-color-bar"
                style={{ background: highlightColor || "#fef08a" }}
                aria-hidden="true"
              />
            </span>
          </ToolbarButton>
          {openPalette === "highlight" ? (
            <ColorPalette
              colors={HIGHLIGHT_COLORS}
              current={highlightColor}
              noneLabel="×"
              onPick={(next) => {
                const chain = chainForMarks(editor);
                if (!next) chain.unsetHighlight().run();
                else chain.setHighlight({ color: next }).run();
                setOpenPalette(null);
              }}
              onCustom={(next) => {
                chainForMarks(editor).setHighlight({ color: next }).run();
                setOpenPalette(null);
              }}
            />
          ) : null}
        </div>
        <span className="al-cb-rte-sep" aria-hidden="true" />
        <ToolbarButton
          label="Undo"
          disabled={disabled || !editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
        >
          <Undo2 strokeWidth={2} size={15} />
        </ToolbarButton>
        <ToolbarButton
          label="Redo"
          disabled={disabled || !editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
        >
          <Redo2 strokeWidth={2} size={15} />
        </ToolbarButton>
        {preview ? (
          <>
            <span className="al-cb-rte-sep" aria-hidden="true" />
            <ToolbarButton
              label="Preview as student"
              disabled={previewEmpty}
              onClick={() => setPreviewOpen(true)}
            >
              <Eye strokeWidth={2} size={15} />
            </ToolbarButton>
          </>
        ) : null}
      </div>
      <div className="al-cb-rte-body" ref={bodyRef}>
        <EditorContent editor={editor} />
        <TableFloat editor={editor} hostRef={bodyRef} disabled={disabled} />
      </div>
      <StudentPreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)}>
        {previewEmpty ? (
          <p>Nothing to preview yet.</p>
        ) : (
          <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
        )}
      </StudentPreviewDialog>
    </div>
  );
});

RichTextEditor.displayName = "RichTextEditor";

export default RichTextEditor;
